import React, { useState, useEffect } from 'react';
import { Layout as AntLayout, Menu, Avatar, Dropdown, Button, Typography, Badge, Drawer, Switch } from 'antd';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined,
  ShopOutlined,
  GiftOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  TeamOutlined,
  MessageOutlined,
  BarChartOutlined,
  DollarOutlined,
  ThunderboltOutlined,
  BellOutlined,
  FileTextOutlined,
  InboxOutlined,
  ExclamationCircleOutlined,
  FormOutlined,
  AppstoreOutlined,
  HistoryOutlined,
  MoonOutlined,
  SunOutlined,
  SendOutlined,
  LineChartOutlined,
  ApiOutlined,
  CloudDownloadOutlined,
  MobileOutlined
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { notificationAPI } from '../services/api';
import GlobalSearch from './GlobalSearch';
import { Select } from 'antd';

const { Header, Sider, Content } = AntLayout;
const { Text } = Typography;

const Layout: React.FC = () => {
  const { stationOwner, subUser, userType, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { currentLanguage, changeLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth < 768) {
        setCollapsed(true);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    // Bildirim sayısını yükle
    const loadNotifications = async () => {
      try {
        const response = await notificationAPI.getNotifications({ isRead: 'false', limit: 1 });
        setUnreadNotifications(response.unreadCount || 0);
      } catch (error) {
        // Hata durumunda sessizce devam et
      }
    };
    
    loadNotifications();
    // Her 30 saniyede bir yenile
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  // Alt kullanıcılar için menü öğelerini filtrele
  const baseMenuItems = [
    {
      key: '/panel',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
    {
      key: '/panel/stations',
      icon: <ShopOutlined />,
      label: 'İstasyonlarım',
    },
    {
      key: '/panel/campaigns',
      icon: <GiftOutlined />,
      label: 'Kampanyalarım',
    },
    ...(userType === 'owner' ? [{
      key: '/panel/subusers',
      icon: <TeamOutlined />,
      label: 'Alt Kullanıcılar',
    }] : []),
    {
      key: '/panel/messages',
      icon: <MessageOutlined />,
      label: 'Mesajlar',
    },
    {
      key: '/panel/notifications',
      icon: <BellOutlined />,
      label: (
        <span>
          Bildirimler
          {unreadNotifications > 0 && (
            <Badge count={unreadNotifications} size="small" style={{ marginLeft: 8 }} />
          )}
        </span>
      ),
    },
    {
      key: '/panel/notification-service',
      icon: <SendOutlined />,
      label: 'Bildirim Servisi',
    },
    {
      key: '/panel/analytics',
      icon: <BarChartOutlined />,
      label: 'Analitik',
    },
    {
      key: '/panel/competition-analysis',
      icon: <LineChartOutlined />,
      label: 'Rekabet Analizi',
    },
    {
      key: '/panel/api-docs',
      icon: <ApiOutlined />,
      label: 'API Dokümantasyonu',
    },
    {
      key: '/panel/backup-restore',
      icon: <CloudDownloadOutlined />,
      label: 'Yedekleme ve Geri Yükleme',
    },
    {
      key: '/panel/payment-subscription',
      icon: <DollarOutlined />,
      label: 'Ödeme & Abonelik',
    },
    {
      key: '/panel/mobile-integration',
      icon: <MobileOutlined />,
      label: 'Mobil Entegrasyon',
    },
    {
      key: '/panel/financial',
      icon: <DollarOutlined />,
      label: 'Finansal',
    },
    {
      key: '/panel/fuel-prices',
      icon: <DollarOutlined />,
      label: 'Yakıt Fiyatları',
    },
    {
      key: '/panel/stocks',
      icon: <ShopOutlined />,
      label: 'Stok Yönetimi',
    },
    {
      key: '/panel/transactions',
      icon: <DollarOutlined />,
      label: 'Gelir/Gider',
    },
    {
      key: '/panel/ev-charging-prices',
      icon: <ThunderboltOutlined />,
      label: 'EV Şarj Fiyatları',
    },
    {
      key: '/panel/station-management',
      icon: <ShopOutlined />,
      label: 'İstasyon Yönetimi',
    },
    {
      key: '/panel/employees',
      icon: <UserOutlined />,
      label: 'Personel Yönetimi',
    },
    {
      key: '/panel/alerts',
      icon: <ThunderboltOutlined />,
      label: 'Otomatik Uyarılar',
    },
    {
      key: '/panel/customers',
      icon: <UserOutlined />,
      label: 'Müşteriler',
    },
    {
      key: '/panel/invoices',
      icon: <FileTextOutlined />,
      label: 'Faturalar',
    },
    {
      key: '/panel/suppliers',
      icon: <ShopOutlined />,
      label: 'Tedarikçiler',
    },
    {
      key: '/panel/inventory',
      icon: <InboxOutlined />,
      label: 'Envanter',
    },
    {
      key: '/panel/complaints',
      icon: <ExclamationCircleOutlined />,
      label: 'Şikayetler',
    },
    {
      key: '/panel/surveys',
      icon: <FormOutlined />,
      label: 'Anketler',
    },
    {
      key: '/panel/scheduled-reports',
      icon: <FileTextOutlined />,
      label: 'Otomatik Raporlar',
    },
    {
      key: '/panel/bulk-operations',
      icon: <AppstoreOutlined />,
      label: 'Toplu İşlemler',
    },
    {
      key: '/panel/activity-logs',
      icon: <HistoryOutlined />,
      label: 'Aktivite Logları',
    },
    {
      key: '/panel/profile',
      icon: <UserOutlined />,
      label: 'Profil',
    },
    ...(userType === 'owner' ? [{
      key: '/panel/settings',
      icon: <SettingOutlined />,
      label: 'Ayarlar',
    }] : []),
  ];

  const menuItems = baseMenuItems;

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profil Ayarları',
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Ayarlar',
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Çıkış Yap',
      danger: true,
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  const handleUserMenuClick = ({ key }: { key: string }) => {
    if (key === 'logout') {
      logout();
      navigate('/');
    } else if (key === 'profile') {
      navigate('/panel/profile');
    } else if (key === 'settings') {
      navigate('/panel/settings');
    }
  };

  const menuContent = (
    <>
      <div style={{ 
        padding: '24px', 
        textAlign: 'center',
        borderBottom: '1px solid #f0f0f0'
      }}>
        <ShopOutlined style={{ fontSize: '32px', color: '#DC143C' }} />
        <div style={{ marginTop: '8px' }}>
          <Text strong style={{ fontSize: '18px', color: '#333' }}>
            YakıtGo
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            İstasyon Paneli
          </Text>
        </div>
      </div>
      
      <Menu
        mode="inline"
        selectedKeys={[location.pathname]}
        items={menuItems}
        onClick={(e) => {
          handleMenuClick(e);
          if (isMobile) {
            setMobileMenuVisible(false);
          }
        }}
        style={{ 
          border: 'none',
          marginTop: '16px'
        }}
      />
    </>
  );

  return (
    <AntLayout style={{ minHeight: '100vh' }}>
      {isMobile ? (
        <Drawer
          title="Menü"
          placement="left"
          onClose={() => setMobileMenuVisible(false)}
          open={mobileMenuVisible}
          styles={{ body: { padding: 0 } }}
          width={280}
        >
          {menuContent}
        </Drawer>
      ) : (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          breakpoint="lg"
          collapsedWidth={isMobile ? 0 : 80}
          style={{
            background: '#fff',
            boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
            overflow: 'auto',
            height: '100vh',
            position: 'fixed',
            left: 0,
            zIndex: 100,
          }}
        >
          {menuContent}
        </Sider>
      )}

      <AntLayout style={{ 
        marginLeft: isMobile ? 0 : (collapsed ? 80 : 200), 
        transition: 'margin-left 0.2s',
        minHeight: '100vh'
      }}>
        <Header
          style={{
            background: '#fff',
            padding: isMobile ? '0 16px' : '0 24px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'sticky',
            top: 0,
            zIndex: 10,
            flexWrap: 'wrap',
            gap: '8px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
            <Button
              type="text"
              icon={isMobile ? <MenuUnfoldOutlined /> : (collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />)}
              onClick={() => isMobile ? setMobileMenuVisible(true) : setCollapsed(!collapsed)}
              style={{ fontSize: '16px', width: 40, height: 40, flexShrink: 0 }}
            />
            <Text strong style={{ 
              fontSize: isMobile ? '16px' : '20px', 
              color: '#333', 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'nowrap',
              flex: 1,
              minWidth: 0
            }}>
              {stationOwner?.companyName || 'YakıtGo İstasyon Paneli'}
            </Text>
          </div>

          {!isMobile && (
            <div style={{ flex: 1, maxWidth: 400, margin: '0 16px' }}>
              <GlobalSearch />
            </div>
          )}

          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: isMobile ? '8px' : '16px', 
            flexShrink: 0 
          }}>
            {/* Dil Değiştirme */}
            {!isMobile && (
              <Select
                value={currentLanguage}
                onChange={(lang) => {
                  changeLanguage(lang);
                  setTimeout(() => {
                    window.location.reload();
                  }, 500);
                }}
                style={{ width: 100 }}
                size="small"
              >
                <Select.Option value="tr">🇹🇷 TR</Select.Option>
                <Select.Option value="en">🇬🇧 EN</Select.Option>
              </Select>
            )}
            
            {/* Dark Mode Toggle */}
            <Switch
              checked={isDark}
              onChange={toggleTheme}
              checkedChildren={<MoonOutlined />}
              unCheckedChildren={<SunOutlined />}
              style={{ marginRight: isMobile ? 0 : 8 }}
            />
            
            {/* Bildirim Butonu */}
            <Button
              type="text"
              icon={
                <Badge count={unreadNotifications} size="small">
                  <BellOutlined style={{ fontSize: '18px' }} />
                </Badge>
              }
              onClick={() => navigate('/panel/notifications')}
              style={{ padding: '4px 8px' }}
            />
            
            {!isMobile && (
              <div style={{ textAlign: 'right', minWidth: '120px' }}>
                <Text strong style={{ display: 'block', color: '#333', fontSize: '14px', lineHeight: '1.5' }}>
                  {userType === 'subuser' && subUser
                    ? `${subUser.firstName} ${subUser.lastName}`
                    : stationOwner?.firstName && stationOwner?.lastName 
                    ? `${stationOwner.firstName} ${stationOwner.lastName}`
                    : 'Kullanıcı'
                  }
                </Text>
                <Text type="secondary" style={{ fontSize: '12px', display: 'block', lineHeight: '1.5' }}>
                  {userType === 'subuser' 
                    ? `Alt Kullanıcı - ${subUser?.role || ''}`
                    : stationOwner?.subscriptionStatus === 'premium' ? 'Premium Üye' : 'Temel Üye'
                  }
                </Text>
              </div>
            )}
            
            <Dropdown
              menu={{
                items: userMenuItems,
                onClick: handleUserMenuClick,
              }}
              placement="bottomRight"
            >
              <Button type="text" style={{ padding: '4px' }}>
                <Avatar 
                  size={isMobile ? 'default' : 'large'}
                  style={{ 
                    backgroundColor: '#DC143C',
                    cursor: 'pointer'
                  }}
                  icon={!stationOwner?.firstName && <UserOutlined />}
                >
                  {stationOwner?.firstName?.[0]}{stationOwner?.lastName?.[0]}
                </Avatar>
              </Button>
            </Dropdown>
          </div>
        </Header>

        <Content style={{
          margin: isMobile ? '16px 8px' : '24px',
          padding: isMobile ? '16px' : '24px',
          background: '#f0f2f5',
          minHeight: 'calc(100vh - 64px)',
        }}>
          <div style={{
            background: '#fff',
            padding: isMobile ? '16px' : '24px',
            borderRadius: '8px',
            minHeight: '100%',
          }}>
            <Outlet />
          </div>
        </Content>
      </AntLayout>
    </AntLayout>
  );
};

export default Layout;
